export type FilterOption = { value: string; label: string }
export type FilterDef = {
  key: string 
  label: string
  kind: "select" | "text"
  options?: FilterOption[]
}

export const ANY = "__any__"

const JURISDICTION: FilterOption[] = [
  { value: "F", label: "Federal Appellate" },
  { value: "FD", label: "Federal District" },
  { value: "FB", label: "Federal Bankruptcy" },
  { value: "FBP", label: "Federal Bankruptcy Panel" },
  { value: "FS", label: "Federal Special" },
  { value: "S", label: "State Supreme" },
  { value: "SA", label: "State Appellate" },
  { value: "ST", label: "State Trial" },
  { value: "SS", label: "State Special" },
  { value: "SAG", label: "State Attorney General" },
  { value: "TRS", label: "Tribal Supreme" },
  { value: "TRA", label: "Tribal Appellate" },
  { value: "TRT", label: "Tribal Trial" },
  { value: "TRX", label: "Tribal Special" },
  { value: "TS", label: "Territory Supreme" },
  { value: "TA", label: "Territory Appellate" },
  { value: "TT", label: "Territory Trial" },
  { value: "TSP", label: "Territory Special" },
  { value: "MA", label: "Military Appellate" },
  { value: "MT", label: "Military Trial" },
  { value: "C", label: "Committee" },
  { value: "I", label: "International" },
  { value: "T", label: "Testing" },
]

const GENDER: FilterOption[] = [
  { value: "m", label: "Male" },
  { value: "f", label: "Female" },
  { value: "o", label: "Other" },
]

const RACE: FilterOption[] = [
  { value: "w", label: "White" },
  { value: "b", label: "Black or African American" },
  { value: "i", label: "American Indian or Alaska Native" },
  { value: "a", label: "Asian" },
  { value: "p", label: "Native Hawaiian or Other Pacific Islander" },
  { value: "mena", label: "Middle Eastern/North African" },
  { value: "h", label: "Hispanic/Latino" },
  { value: "o", label: "Other" },
]

const PARTY: FilterOption[] = [
  { value: "d", label: "Democratic" },
  { value: "r", label: "Republican" },
  { value: "i", label: "Independent" },
  { value: "g", label: "Green" },
  { value: "l", label: "Libertarian" },
  { value: "f", label: "Federalist" },
  { value: "w", label: "Whig" },
  { value: "j", label: "Jeffersonian Republican" },
  { value: "u", label: "National Union" },
  { value: "z", label: "Reform Party" },
]

// Common judicial position types (the full CourtListener set is much larger).
const POSITION_TYPE: FilterOption[] = [
  { value: "jud", label: "Judge" },
  { value: "c-jud", label: "Chief Judge" },
  { value: "ass-jud", label: "Associate Judge" },
  { value: "pres-jud", label: "Presiding Judge" },
  { value: "jus", label: "Justice" },
  { value: "c-jus", label: "Chief Justice" },
  { value: "act-jud", label: "Acting Judge" },
  { value: "mag", label: "Magistrate" },
  { value: "chan", label: "Chancellor" },
  { value: "com", label: "Commissioner" },
  { value: "spec-m", label: "Special Master" },
]

const RELIGION: FilterOption[] = [
  { value: "ca", label: "Catholic" },
  { value: "pr", label: "Protestant" },
  { value: "je", label: "Jewish" },
  { value: "mu", label: "Muslim" },
  { value: "at", label: "Atheist" },
  { value: "ag", label: "Agnostic" },
  { value: "mo", label: "Mormon" },
  { value: "bu", label: "Buddhist" },
  { value: "hi", label: "Hindu" },
  { value: "ep", label: "Episcopalian" },
]

const US_STATES: FilterOption[] = (
  [
    ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
    ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
    ["DC", "District of Columbia"], ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"],
    ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"],
    ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"],
    ["MD", "Maryland"], ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"],
    ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"],
    ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"],
    ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"],
    ["OK", "Oklahoma"], ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"],
    ["SC", "South Carolina"], ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"],
    ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"],
    ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
  ] as const
).map(([value, label]) => ({ value, label }))

const LAW_JURISDICTION: FilterOption[] = [
  { value: "federal", label: "Federal" },
  ...[...US_STATES, { value: "PR", label: "Puerto Rico" }]
    .map(({ value, label }) => ({ value: value.toLowerCase(), label }))
    .sort((a, b) => a.label.localeCompare(b.label)),
]

export const lawFilters: FilterDef[] = [
  { key: "state", label: "Jurisdiction", kind: "select", options: LAW_JURISDICTION },
]

export const courtFilters: FilterDef[] = [
  { key: "jurisdiction", label: "Jurisdiction", kind: "select", options: JURISDICTION },
  {
    key: "inUse",
    label: "Whether in use",
    kind: "select",
    options: [
      { value: "1", label: "In use" },
      { value: "0", label: "Not in use" },
    ],
  },
  {
    key: "active",
    label: "Status",
    kind: "select",
    options: [
      { value: "active", label: "Active" },
      { value: "historical", label: "Historical" },
    ],
  },
  { key: "parent", label: "Parent court…", kind: "text" },
]

export const judgeFilters: FilterDef[] = [
  { key: "court", label: "Court served…", kind: "text" },
  { key: "positionType", label: "Position type", kind: "select", options: POSITION_TYPE },
  { key: "onBench", label: "Serving status", kind: "select", options: [{ value: "1", label: "Still serving" }] },
  { key: "party", label: "Political affiliation", kind: "select", options: PARTY },
  { key: "school", label: "School…", kind: "text" },
  { key: "gender", label: "Gender", kind: "select", options: GENDER },
  { key: "race", label: "Race", kind: "select", options: RACE },
  { key: "religion", label: "Religion", kind: "select", options: RELIGION },
  { key: "birthState", label: "Birth state", kind: "select", options: US_STATES },
]
